import type { AppError } from '@/features/auth/types';
import type {
  EvergreenScheduledInteractionType,
  EvergreenTimelineEvent,
} from '@/features/webinar/types';
import { isHideEvent, resolveDisplayEventType } from '@/features/webinar/evergreenTimelineUtils';

export type Hms = { h: string; m: string; s: string };

export type ScheduledWindow = { id: number; startSeconds: number; endSeconds: number };

export type ScheduleError =
  | 'INVALID_TIME'
  | 'END_BEFORE_START'
  | 'OUTSIDE_VIDEO'
  | 'OVERLAP'
  | 'VIDEO_NOT_READY'
  | 'RESOURCE_INVALID'
  | 'SESSION_IN_PROGRESS'
  | 'GENERIC';

export const SCHEDULE_ERRORS: readonly ScheduleError[] = [
  'INVALID_TIME',
  'END_BEFORE_START',
  'OUTSIDE_VIDEO',
  'OVERLAP',
  'VIDEO_NOT_READY',
  'RESOURCE_INVALID',
  'SESSION_IN_PROGRESS',
  'GENERIC',
];

const SCHEDULABLE_TYPES: readonly EvergreenScheduledInteractionType[] = [
  'OFFER',
  'SURVEY',
  'QUIZ',
  'SHARED_RESOURCE',
];

export function isSchedulableInteractionType(
  type: string | null | undefined,
): type is EvergreenScheduledInteractionType {
  return type != null && (SCHEDULABLE_TYPES as readonly string[]).includes(type);
}

export function secondsToHms(seconds: number): Hms {
  if (!Number.isFinite(seconds) || seconds < 0) return { h: '00', m: '00', s: '00' };
  const pad = (value: number) => value.toString().padStart(2, '0');
  return {
    h: pad(Math.floor(seconds / 3600)),
    m: pad(Math.floor((seconds % 3600) / 60)),
    s: pad(Math.floor(seconds % 60)),
  };
}

/** Renvoie `null` si un champ n'est pas un entier ou si minutes/secondes dépassent 59. */
export function hmsToSeconds({ h, m, s }: Hms): number | null {
  const parts = [h, m, s].map((value) => (value.trim() === '' ? 0 : Number(value)));
  if (parts.some((value) => !Number.isInteger(value) || value < 0)) return null;
  const [hours, minutes, seconds] = parts;
  if (minutes > 59 || seconds > 59) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

export function validateScheduleWindow(
  startSeconds: number | null,
  endSeconds: number | null,
  videoDurationSeconds: number | null,
): ScheduleError | null {
  if (startSeconds == null || endSeconds == null) return 'INVALID_TIME';
  if (endSeconds <= startSeconds) return 'END_BEFORE_START';
  if (videoDurationSeconds != null && endSeconds > videoDurationSeconds) return 'OUTSIDE_VIDEO';
  return null;
}

/**
 * Créneaux programmés d'une ressource. Un Live converti produit une apparition sans fin puis un
 * masquage : chaque masquage ferme la plus ancienne apparition ouverte, comme côté back. Une
 * apparition jamais fermée court jusqu'à la fin de la vidéo, tant que sa durée est connue.
 */
export function getScheduledWindows(
  timeline: EvergreenTimelineEvent[],
  type: EvergreenScheduledInteractionType,
  resourceId: number,
  videoDurationSeconds: number | null,
): ScheduledWindow[] {
  const events = timeline
    .filter((event) => event.resourceId === resourceId && resolveDisplayEventType(event) === type)
    .sort((a, b) => a.startOffsetSeconds - b.startOffsetSeconds || a.displayOrder - b.displayOrder);
  const windows: ScheduledWindow[] = [];
  const openShows: EvergreenTimelineEvent[] = [];
  for (const event of events) {
    if (event.endOffsetSeconds != null) {
      windows.push({
        id: event.id,
        startSeconds: event.startOffsetSeconds,
        endSeconds: event.endOffsetSeconds,
      });
    } else if (isHideEvent(event)) {
      const show = openShows.shift();
      if (show) {
        windows.push({
          id: show.id,
          startSeconds: show.startOffsetSeconds,
          endSeconds: event.startOffsetSeconds,
        });
      }
    } else {
      openShows.push(event);
    }
  }
  if (videoDurationSeconds != null) {
    for (const show of openShows) {
      windows.push({
        id: show.id,
        startSeconds: show.startOffsetSeconds,
        endSeconds: videoDurationSeconds,
      });
    }
  }
  return windows.sort((a, b) => a.startSeconds - b.startSeconds);
}

const BACK_ERROR_TO_SCHEDULE_ERROR: Record<string, ScheduleError> = {
  'HTTP.ERROR.409.EVERGREEN.INTERACTION.OVERLAPPING_RESOURCE_WINDOW': 'OVERLAP',
  'HTTP.ERROR.400.EVERGREEN.INTERACTION.OFFSET_OUTSIDE_VIDEO': 'OUTSIDE_VIDEO',
  'HTTP.ERROR.400.EVERGREEN.INTERACTION.VIDEO_NOT_READY': 'VIDEO_NOT_READY',
  'HTTP.ERROR.400.EVERGREEN.INTERACTION.END_OFFSET.INVALID': 'END_BEFORE_START',
  'HTTP.ERROR.400.EVERGREEN.INTERACTION.START_OFFSET.INVALID': 'INVALID_TIME',
  'HTTP.ERROR.400.EVERGREEN.INTERACTION.RESOURCE_INVALID': 'RESOURCE_INVALID',
  'HTTP.ERROR.403.EVERGREEN_SESSION_IN_PROGRESS': 'SESSION_IN_PROGRESS',
};

function detailMessages(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details
    .map((detail: unknown) =>
      detail && typeof detail === 'object' && 'message' in detail ? detail.message : null,
    )
    .filter((message): message is string => typeof message === 'string');
}

/**
 * Codes métier d'une erreur, qu'elle soit brute (réponse HTTP) ou déjà rejetée par un thunk. Une
 * erreur de validation porte ses codes dans `details`, sous la racine HTTP.ERROR.400.VALIDATION.ERRORS.
 */
export function getBackErrorCodes(error: unknown): string[] {
  const source = (error ?? {}) as Partial<Record<keyof AppError, unknown>>;
  return [source.apiCode, source.message, ...detailMessages(source.details)].filter(
    (code): code is string => typeof code === 'string' && code !== '',
  );
}

export function toScheduleError(error: AppError | undefined): ScheduleError {
  for (const code of getBackErrorCodes(error)) {
    if (BACK_ERROR_TO_SCHEDULE_ERROR[code]) return BACK_ERROR_TO_SCHEDULE_ERROR[code];
  }
  return 'GENERIC';
}

/**
 * Côté spectateur, une seule interaction de chaque type est affichée : le retrait d'une autre
 * ressource (créneaux qui se chevauchent) ne doit pas effacer celle qui l'a remplacée.
 */
export function shouldHideDisplayedInteraction(
  displayedResourceId: number | null | undefined,
  hiddenResourceId: number,
): boolean {
  return displayedResourceId == null || displayedResourceId === hiddenResourceId;
}
